import React, { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useSnackbar } from 'notistack'

import { CircularProgress, Box, Dialog, Typography, Button } from '@mui/material'

import { useGetEligibleStaffsQuery, useUpdateShiftMutation } from '@/core/services/planning'
import { useAdminClaimShiftMutation, useReleaseShiftMutation } from '@/core/services/shift-marketplace'
import { Shift } from '@/core/models/shift.model'
import { getApiErrorMessage } from '@/utils/errors'

import { ShiftDialog } from './ShiftDialog'

type ShiftDetailsProps = {
  open: boolean
  onClose: () => void
  shift: Shift | null
  planningId: string
  onShiftUpdated?: () => void
}

export function ShiftDetails({ open, onClose, shift, planningId, onShiftUpdated }: ShiftDetailsProps) {
  const { t } = useTranslation()
  const { enqueueSnackbar } = useSnackbar()
  const [isProcessing, setIsProcessing] = useState(false)
  const [localOpen, setLocalOpen] = useState(false)

  // Sync local open state with prop
  useEffect(() => {
    setLocalOpen(open)
  }, [open])

  const handleLocalClose = () => {
    setLocalOpen(false)
    onClose()
  }

  // Fetch eligible staff for the shift
  const { data: eligibleStaff = [], isLoading: isLoadingEligibleStaff } = useGetEligibleStaffsQuery(
    {
      planningId,
      shiftTemplateId: shift?.shiftTemplate?._id || '',
      startTime: shift?.startTime || '',
      endTime: shift?.endTime || '',
    },
    { skip: !shift || !open || !planningId }
  )

  const [updateShift] = useUpdateShiftMutation()
  const [adminClaimShift] = useAdminClaimShiftMutation()
  const [releaseShift] = useReleaseShiftMutation()

  const handleRemoveStaff = async (staffId: string) => {
    if (!shift?._id || !planningId) return

    setIsProcessing(true)
    try {
      await releaseShift({ shiftId: shift._id, staffId }).unwrap()

      enqueueSnackbar(t('page.planning.shift.notifications.staffRemoved'), { variant: 'success' })
      if (onShiftUpdated) {
        onShiftUpdated()
      }
    } catch (error) {
      enqueueSnackbar(getApiErrorMessage(error), { variant: 'error' })
    } finally {
      setIsProcessing(false)
    }
  }

  const handleUpdateShift = async (updatedShift: any) => {
    if (!shift?._id || !planningId) return

    setIsProcessing(true)
    try {
      await updateShift({
        planningId,
        shiftId: shift._id,
        updateShiftParams: updatedShift,
      }).unwrap()

      enqueueSnackbar(t('page.planning.shift.notifications.updated'), { variant: 'success' })
      if (onShiftUpdated) {
        onShiftUpdated()
      }
    } catch (error) {
      enqueueSnackbar(getApiErrorMessage(error), { variant: 'error' })
    } finally {
      setIsProcessing(false)
    }
  }

  const handleAssignEligibleStaff = async (staffIds: string[], callback?: () => void) => {
    if (!shift?._id || !planningId || !staffIds.length) return

    setIsProcessing(true)
    try {
      // Process each staff assignment sequentially
      for (const staffId of staffIds) {
        await adminClaimShift({
          planningId,
          shiftTemplateId: shift?.shiftTemplate?._id || '',
          startTime: shift?.startTime || '',
          endTime: shift?.endTime || '',
          staffId,
        }).unwrap()
      }

      enqueueSnackbar(
        t('page.planning.shift.notifications.staffAssigned', {
          count: staffIds.length,
        }),
        { variant: 'success' }
      )
      callback?.()
      // Make sure to call onShiftUpdated to trigger a refresh in the parent component
      onShiftUpdated?.()
    } catch (error) {
      enqueueSnackbar(getApiErrorMessage(error), { variant: 'error' })
    } finally {
      setIsProcessing(false)
    }
  }

  // Handle loading state
  if (isLoadingEligibleStaff && open) {
    return (
      <Dialog open={localOpen} onClose={handleLocalClose} maxWidth="sm" fullWidth>
        <Box sx={{ p: 3, display: 'flex', justifyContent: 'center' }}>
          <CircularProgress />
        </Box>
      </Dialog>
    )
  }

  // Handle case when shift is not found
  if (!shift && open) {
    return (
      <Dialog open={localOpen} onClose={handleLocalClose} maxWidth="sm" fullWidth>
        <Box sx={{ p: 3 }}>
          <Typography>{t('page.planning.shift.error.notFound', { defaultValue: 'Shift not found' })}</Typography>
        </Box>
      </Dialog>
    )
  }

  // Only render when we have shift data and dialog should be open
  if (!shift || !localOpen) {
    return null
  }

  return (
    <ShiftDialog
      open={localOpen && !!shift}
      onClose={handleLocalClose}
      shift={shift}
      onRemoveStaff={handleRemoveStaff}
      eligibleStaff={eligibleStaff}
      onAssignEligibleStaff={handleAssignEligibleStaff}
      isLoadingEligibleStaff={isLoadingEligibleStaff || isProcessing}
    />
  )
}
